import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  View,
  StyleSheet,
  ActivityIndicator,
  ImageBackground,
  Image,
  TouchableOpacity,
  Platform,
  ScrollView,
} from "react-native";
import Text from "../../../components/Text";
import Theme from "../../../style/Theme";
import { Colors, Routes } from "../../../configs";
import avatarProfileIcon from "../../../images/avatar/ic_account_normal1.png";
import iconSearch from "../../../images/ic_search_normal@3x.png";
import { getStatusBarHeight } from "react-native-iphone-x-helper";
import locales_es from "../../../locales/es";
import { renderAge, renderAgeText } from "../../../modules/ViewHelpers";
import TouchableOpacityHOC from "../../../components/TouchableOpacityHOC";
import InputApp from "../../../components/InputApp";
import ButtonLinear from "../../../components/Buttons/ButtonLinear";
import { SOURCE_ICON } from "../../../images";
import useMyPatientsPage from "../shared/useMyPatientsPage";
import { FAKE_EMAIL_REGEX } from "../../../models/constants";
import Overlay from "../../../components/Overlay";
import SearchBox from "../../../components/Home/SearchBox";
import Layout from "../../../components/Layout/Layout";
import HeaderButton from "../../../components/HeaderButton";
import SearchPatients from "../../SearchPatients";
import ButtonIcon from "../../../components/Buttons/ButtonIcon";
import { height, width } from "../../../configs/Const";
import CloudAddIcon from "../../../assets/img/cloud-add.svg";
import EditIcon from "../../../assets/img/edit-2.svg";
import CustomSelect from "../../../utils/select/CustomSelect";
import Tooltip from "../../../components/Tooltip";
import { IoInformationCircleOutline } from "react-icons/io5";

interface MyPatientsProps {
  source: string;
}

const MyPatientsDesktop = (props: MyPatientsProps) => {

  const {
    selectPatientId,
    patients,
    medicalInsuranceList,
    onPressWhitePlusButton,
    onUserInfoPress,
    goToUserProfile,
    getPatients,
    getMedicalInsurances,
    keySearch,
    history,
    nextPageUrl,
    onKeySearchChange,
    clearSearch,
    refreshing,
    onGoback,
    showFilter,
  } = useMyPatientsPage(props.selectPatientId, props.onSelectPatient);

  const contentTableRef = useRef();
  const [selectedInsurance, setSelectedInsurance] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const onScroll = () => {
    if (contentTableRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentTableRef.current;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (isNearBottom && nextPageUrl) {
        getPatients(selectedInsurance?.value ?? 0, nextPageUrl);
      }


    }
  };


  useEffect(() => {
    if (medicalInsuranceList && medicalInsuranceList.length > 0) {
      setSelectedInsurance(medicalInsuranceList[0]);
    }
  }, [medicalInsuranceList]);

  const handleInsuranceChange = (
    newValue: { value: string; label: string } | null
  ) => {

    if (newValue == null) {
      newValue = { value: "0", label: "Todos" };
    }

    setSelectedInsurance(newValue);

    if (newValue) {
      getPatients(newValue.value);
    }
  };

  const handleSearchChange = (e: any) => {
    onKeySearchChange(e);
    getPatients(selectedInsurance?.value ?? 0);
  };

  useEffect(() => {
    getPatients(selectedInsurance?.value ?? 0);
  }, [selectedInsurance?.value]);

  useEffect(() => {
    const listInnerElement = contentTableRef.current;

    if (listInnerElement) {
      listInnerElement.addEventListener("scroll", onScroll);

      // Clean-up
      return () => {
        listInnerElement.removeEventListener("scroll", onScroll);
      };
    }
  }, [refreshing, nextPageUrl]);

  return (
    <>
      <View style={styles.container}>
        <View style={styles.content}>
          <View style={styles.headerWrapper}>
            {props.source && (
              <HeaderButton
                onPress={() => onGoback(props)}
                style={{
                  marginTop: 26,
                  marginLeft: 24,
                }}
              />
            )}
            <Text size={38} lineHeight={36} bold>
              {locales_es.myPatients_page.title}
            </Text>
          </View>
          <div style={styles.viewSearch}>
            <InputApp
              styleView={styles.inputSearch}
              onChange={onKeySearchChange}
              placeholder="Buscar Pacientes"
              isShowIconLeft
              iconLeft={<Image source={iconSearch} style={styles.iconSearch} />}
              autoCorrect={false}
              autoCapitalize="words"
              value={keySearch}
              showClear={keySearch.length >= 1}
              onClear={() => clearSearch()}
            />

            {/* Select de Instituciones */}

            {
              showFilter &&
                <View style={styles.selectContainer}>
                  <CustomSelect
                    options={medicalInsuranceList || []}
                    value={selectedInsurance}
                    isClearable={selectedInsurance && selectedInsurance.value !== "0"}
                    onChange={handleInsuranceChange}
                  />
                </View>
            }
          </div>
          <ScrollView
            style={{ overflow: "auto", height: "100%" }}
            ref={contentTableRef}
          >
            <View style={styles.tableRowHeader}>
              <View style={[styles.columnHeader, { minWidth: 170, width: 170 }]}>
                <Text color={"#189394"} bold size={18}>
                  {locales_es.myPatients_page.name}
                </Text>
              </View>
              <View style={[styles.columnHeader, { minWidth: 140, width: 140 }]}>
                <Text color={"#189394"} bold size={18}>
                  {locales_es.myPatients_page.id}
                </Text>
              </View>
              <View style={styles.columnHeader}>
                <Text color={"#189394"} bold size={18}>
                  {locales_es.myPatients_page.clinic}
                </Text>
              </View>
              <View
                style={[styles.columnHeader, { minWidth: 100, width: 100 }]}
              >
                <Text color={"#189394"} bold size={18}>
                  {locales_es.myPatients_page.gender}
                </Text>
              </View>
              <View
                style={[styles.columnHeader, { minWidth: 80, width: 80 }]}
              >
                <Text color={"#189394"} bold size={18}>
                  {locales_es.myPatients_page.age}
                </Text>
              </View>
              <View style={[styles.columnHeader, { minWidth: 300, width: 300 }]}>
                <Text color={"#189394"} bold size={18}>
                  {locales_es.myPatients_page.email}
                </Text>
              </View>
              <View style={styles.columnHeader}>
                <Text color={"#189394"} bold size={18}>
                  {locales_es.myPatients_page.insurance}
                </Text>
              </View>
            </View>

            {refreshing || (patients && patients.length) ? (
              <View style={styles.tableWrapper}>
                {patients.map((patient, index) => (
                  <TouchableOpacityHOC
                    onPress={
                      selectPatientId
                        ? () => onUserInfoPress(patient)
                        : () => goToUserProfile(patient)
                    }
                    key={index}
                  >
                    <View
                      style={styles.tableRow}
                      key={`${patient.name}_${patient.lastname}_${patient.identification}_${index}`}
                    >
                      <View
                        style={[
                          styles.column,
                          { minWidth: 175, width: 175 },
                        ]}
                      >
                        <ImageBackground
                          source={{
                            uri:
                              patient.full_profile_image || avatarProfileIcon,
                          }}
                          resizeMode="cover"
                          imageStyle={styles.avatarImage}
                        />
                        <Text size={14}>
                          {patient.name} {patient.lastname}
                        </Text>
                      </View>
                      <View style={[styles.column, { minWidth: 140, width: 140 }]}>
                        <Text size={14}>
                          {patient.identification || "No Especificada"}
                        </Text>
                      </View>
                      <View style={styles.column}>
                        <Text
                          size={14}
                          color={
                            patient.clinic?.name
                              ? Colors.BlueTag
                              : Colors.GreenTag
                          }
                        >
                          <img
                            src={patient.clinic?.name ? CloudAddIcon : EditIcon}
                            style={{
                              marginRight: 5,
                              width: patient.clinic?.name ? 24 : 20,
                              height: patient.clinic?.name ? 24 : 20,
                            }}
                          />
                          {patient.clinic?.name ?? "Propio"}
                        </Text>
                      </View>
                      <View
                        style={[styles.column, { minWidth: 100, width: 100 }]}
                      >
                        <Text size={14}>
                          {patient.gender?.name || "No Especificado"}
                        </Text>
                      </View>
                      <View
                        style={[styles.column, { minWidth: 80, width: 80 }]}
                      >
                        <Text size={14}>
                          {renderAgeText(
                            patient.birth_date,
                            true,
                            true,
                            true
                          ).trim() || "No Especificada"}
                        </Text>
                      </View>
                      <View style={[styles.column, styles.emailColumn]}>
                        <Text size={14}>
                          {FAKE_EMAIL_REGEX.test(patient.email)
                            ? "No Especificado"
                            : patient.email}{" "}
                        </Text>
                        {patient.masked_contact_details && patient.email && (
                          <View style={styles.tooltipWrapper}>
                            <Tooltip
                              fontSize="0.85rem"
                              width="180px"
                              text="Este dato está protegido. Consulte con su institución si requiere acceso."
                              backgroundColor="white"
                              color="black"
                              border="1px solid black"
                              transform="translateX(20%) translateY(-20%)"
                            >
                              <IoInformationCircleOutline
                                size={25}
                                color={Colors.TealBlue}
                              />
                            </Tooltip>
                          </View>
                        )}
                        <br />
                      </View>

                      <View style={[styles.column, { zIndex: -10 }]}>
                        <Text size={14}>
                          {patient.medical_insurance?.name || "Sin cobertura"}
                        </Text>
                      </View>
                    </View>
                  </TouchableOpacityHOC>
                ))}
                {refreshing ? (
                  <ActivityIndicator style={{ marginVertical: 24 }} />
                ) : null}
              </View>
            ) : (
              <Text
                size={14}
                lineHeight={20}
                marginTop={30}
                center
                style={{ marginHorizontal: 24 }}
              >
            {locales_es.myPatients_page.not_found_patients}
              </Text>
            )}
          </ScrollView>
        </View>
        <View
          style={{
            justifyContent: "right",
            alignItems: "end",
            marginBottom: 20,
          }}
        >
          <ButtonIcon
            text={locales_es.myPatients_page.new}
            onPress={onPressWhitePlusButton}
            icon={"plus"}
            imgStyle={styles.prescriptionIcon}
            style={{
              backgroundColor: "#39BFB2",
              height: 40,
              width: 120,
              paddingLeft: 0,
              borderRadius: 50,
              position: "fixed",
              right: 20,
              bottom: 10,
            }}
            iconPosition="left"
            tintColor={Colors.White}
          />
        </View>
      </View>
    </>
  );
};

export default MyPatientsDesktop;

const styles = StyleSheet.create({
  container: {
    minHeight: "95%",
    zIndex: 1000,
  },
  content: {
    overflow: "scroll",
    overflowY: "scroll",
    width: "105%",
    minHeight: "100%",
    height: "100%",
    paddingBottom: 14,
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: getStatusBarHeight(),
  },
  viewSearch: {
    display: "flex",
    flexDirection: "row",
  },
  headerWrapper: { flexDirection: "row", gap: 16, alignItems: "center" },
  tableRowHeader: { display: "flex", flexFlow: "row", height: "10%" },
  columnHeader: {
    minWidth: 150,
    width: "calc(100% / 6)",
    wordBreak: "break-word",
    padding: 16,
    paddingLeft: 0,
  },
  tableRow: { display: "flex", flexFlow: "row" },
  column: {
    minWidth: 150,
    wordBreak: "break-word",
    width: "calc(100% / 6)",
    padding: "16px 16px 16px 0",
    display: "flex",
    flexDirection: "row",
    gap: 16,
    alignItems: "center",
    justifyContent: "flex-start",
    marginBottom: 12,
  },
  tableWrapper: {
    maxHeight: "calc(100vh - 300px)",
    height: "10%",
    marginTop: 24,
  },
  avatarImage: {
    borderRadius: 20,
    width: 40,
    height: 40,
    position: "relative",
  },
  // Select style
  inputSelectContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 24,
  },
  selectContainer: {
    width: 200,
    marginLeft: 10,
    paddingVertical: 24,
    justifyContent: "center"
  },
  // Select style
  iconSearch: {
    ...Theme.icons,
    tintColor: Colors.DodgerBlue,
  },
  inputSearch: { maxWidth: 300, paddingTop: 24, paddingBottom: 24 },
  prescriptionActionWrapper: {
    position: "fixed",
    right: 24,
    bottom: 16,
  },
  prescriptionAction: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "capitalize",
  },
  prescriptionButton: {
    borderRadius: 20,
    height: 40,
    width: 40,
    marginTop: 0,
    marginLeft: 8,
  },
  prescriptionIcon: {
    width: 20,
    height: 20,
    tintColor: "white",
    marginLeft: 0,
  },
  headerLayout: {
    backgroundColor: "transparent",
    shadowColor: "transparent",
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
    paddingTop: Platform.OS === "ios" ? getStatusBarHeight() : 0,
    ...Theme.flexRowSpace,
    paddingBottom: 10,
  },
  emailColumn: {
    // flex: 1,
    position: "relative",
    zIndex: 1000,
    paddingLeft: 0,
    minWidth: 300, 
    width: 300

  },
  tooltipWrapper: {
    position: "relative",
    zIndex: 1000,
    marginLeft: 8,
  },
});
